import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { Button } from "@/components/_atoms/button";
import { IconInvisible } from "@/components/_icons/IconInvisible";
import { SidebarNavElement } from "@/components/_templates/sidebar/sidebar-nav-element";
import { TeamIcon } from "@/domain/team/components/team-icon";
import { makeTeamSettingsUrl } from "@/domain/team/lib/make-team-url";
import { Team } from "@/domain/team/models/team-model";
import { Workspace } from "@/domain/workspace/models/workspace-model";
import { cn } from "@/lib/cn";

export interface SidebarSettingsTeamNavElementProps {
  workspace: Workspace;
  team: Team;
  expanded?: boolean | undefined;
}

export const SidebarSettingsTeamNavElement = ({ workspace, team, ...props }: SidebarSettingsTeamNavElementProps) => {
  const { t } = useTranslation("common");
  const [expanded, setExpanded] = useState(props.expanded !== undefined ? props.expanded : false);

  const { teamId } = useParams();

  useEffect(() => {
    if (teamId === team.id) {
      setExpanded(true);
    }
  }, [teamId, team]);

  if (!team) {
    return <></>;
  }

  return (
    <>
      <div className="relative m-1 rounded-sm">
        <Button
          variant="ghost"
          className={cn(
            "flex h-auto w-full select-none flex-row items-center justify-start overflow-hidden text-ellipsis rounded-sm border-none bg-transparent p-0 text-sm font-normal hover:bg-foreground/3",
          )}
          onClick={() => {
            setExpanded(!expanded);
          }}
          aria-expanded={expanded}
          aria-label={t("a11y.teamToggle", { name: team.name })}
        >
          <span className="rounded-sm pl-1">
            <TeamIcon team={team} />
          </span>
          <div className="flex min-w-0">
            <div className="pointer-events-none h-8 items-center truncate rounded-sm pb-0 pl-1.5 pr-0.5 pt-0 text-sm leading-8">
              {team.name}
            </div>
          </div>
          <ChevronDown className={`ml-1 mr-1 h-4 w-4 text-muted-foreground ${expanded ? "" : "-rotate-90"}`} />
        </Button>
      </div>
      {expanded && (
        <>
          <SidebarNavElement to={makeTeamSettingsUrl({ workspace, team, path: "general" })}>
            <IconInvisible className="mr-2 h-3 w-3" />
            {t("sidebar.general")}
          </SidebarNavElement>

          <SidebarNavElement to={makeTeamSettingsUrl({ workspace, team, path: "members" })}>
            <IconInvisible className="mr-2 h-3 w-3" />
            {t("sidebar.members")}
          </SidebarNavElement>
        </>
      )}
    </>
  );
};
